import { default as FileSaver } from 'file-saver';
import { html2canvas } from 'html2canvas-pro';
import { Point } from '@app/types';
import { CustomDiagramWidget } from '@app/widgets/CustomDiagramWidget';
import { Utility } from '../../Utility';

// Pixels ignored while trimming when alpha is below this.
const alphaThreshold = 10;
// Chunk size for base64 conversion, avoids call stack limits.
const base64Chunk = 0x8000;
// Pixel counts as content when its color distance from the background exceeds this.
const colorThreshold = 24;
// Margin in px kept around the flow in the exported image.
const captureMargin = 60;
// Extra offset in px when nodes have negative coordinates.
const edgeMargin = 20;
// Margin in px between the canvas edge and the outermost nodes while capturing.
const flowMargin = 120;
// Icon font served from public lib, inlined because the capture sandbox cannot fetch it.
const fontBase = '/lib/feather-icons-font/';
// Pixel counts as content when its channel spread (saturation) exceeds this.
const spreadThreshold = 28;
const transparent = 'rgba(0, 0, 0, 0)';
// Below the application overlay (z-index 5000) so the capture stays hidden behind it.
const hostStyle = 'left:0;overflow:visible;position:fixed;top:0;z-index:1000;';
// Elements excluded from the exported image.
const hiddenClasses = [
  'error-bar',
  'error-bar-container',
  'icon-external-link',
  'icon-recycle',
  'icon-sliders',
  'link-terminal',
  'point'
];
// Elements measured to find the rendered flow bounds.
const boundsSelector = [
  '.node',
  'svg line',
  'svg path',
  'svg polygon',
  'svg polyline',
  'svg text'
].join(',');

let captureFontCss: string | null = null;

/** Resolves after the given number of animation frames elapse, giving the DOM time to settle after style/layout changes before measuring or capturing. */
function waitFrames(count = 2): Promise<void> {
  return new Promise((resolve) => {
    const step = (remaining: number) => {
      if (remaining <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(() => step(remaining - 1));
    };
    step(count);
  });
}

/** Converts a canvas to a PNG `Blob`, rejecting if the canvas produces no data. */
function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error('Canvas produced an empty blob'));
    }, 'image/png');
  });
}

/** Represents a color based on its red, green, & blue light levels. */
type Rgb = {
  b: number;
  g: number;
  r: number;
};

/** Resolves a CSS color string to its RGB components by painting it onto a 1x1 canvas and reading the pixel back. */
function parseColor(color: string | null): Rgb {
  if (!color) {
    return { b: 0, g: 0, r: 0 };
  }

  const probe = document.createElement('canvas');
  probe.width = 1;
  probe.height = 1;
  const context = probe.getContext('2d')!;
  context.fillStyle = color;
  context.fillRect(0, 0, 1, 1);
  const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
  return { b, g, r };
}

/**
 * Crops a canvas to the bounding box of its non-background content plus a margin.
 *
 * @note Returns the source canvas unchanged if no content is detected.
 */
function trimCanvas(
  source: HTMLCanvasElement,
  backgroundColor: string | null
): HTMLCanvasElement {
  const context = source.getContext('2d')!;
  const { data, height, width } = context.getImageData(0, 0, source.width, source.height);
  const background = parseColor(backgroundColor);
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = ((y * width) + x) * 4;

      if (data[i + 3] < alphaThreshold) {
        continue;
      }

      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const distance = Math.hypot(r - background.r, g - background.g, b - background.b);
      const spread = Math.max(r, g, b) - Math.min(r, g, b);

      if (distance <= colorThreshold && spread <= spreadThreshold) {
        continue;
      }

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (maxX <= minX || maxY <= minY) {
    return source;
  }

  const cropX = Math.max(0, minX - captureMargin);
  const cropY = Math.max(0, minY - captureMargin);
  const cropWidth = Math.min(width, maxX + captureMargin + 1) - cropX;
  const cropHeight = Math.min(height, maxY + captureMargin + 1) - cropY;
  const trimmed = document.createElement('canvas');
  trimmed.width = cropWidth;
  trimmed.height = cropHeight;
  trimmed.getContext('2d')!.drawImage(
    source,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );
  return trimmed;
}

/**
 * Temporarily reparents the canvas element into an off-screen host so it can be rasterized cleanly.
 *
 * @returns A function that restores it to its original position.
 */
function mountCaptureHost(canvasEl: HTMLCanvasElement): () => void {
  const host = document.createElement('div');
  host.dataset.diagramCaptureHost = 'true';
  host.style.cssText = hostStyle;
  const parent = canvasEl.parentNode!;
  const next = canvasEl.nextSibling;
  document.body.appendChild(host);
  host.appendChild(canvasEl);

  return () => {
    if (next) {
      parent.insertBefore(canvasEl, next);
    } else {
      parent.appendChild(canvasEl);
    }
    host.remove();
  };
}

/** Rasterizes the canvas element with html2canvas-pro. */
async function rasterizeCanvas(
  canvasEl: HTMLCanvasElement,
  backgroundColor: string | null
): Promise<HTMLCanvasElement> {
  const options: Parameters<typeof html2canvas>[1] = {
    backgroundColor,
    ignoreElements: ({ classList, tagName }) => {
      return (
        tagName === 'IMG'
        || tagName === 'SCRIPT'
        || hiddenClasses.some((name) => classList.contains(name))
      );
    },
    logging: false,
    scale: 1,
    scrollX: 0,
    scrollY: 0,
    useCORS: true
  };

  try {
    return await html2canvas(canvasEl, {
      ...options,
      foreignObjectRendering: true
    });
  } catch (_error) {
    return await html2canvas(canvasEl, {
      ...options,
      foreignObjectRendering: false
    });
  }
}

function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += base64Chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + base64Chunk));
  }
  return btoa(binary);
}

/** Lazily fetches and caches the feather-icons stylesheet. */
async function getCaptureFontCss(): Promise<string> {
  if (captureFontCss != null) {
    return captureFontCss;
  }

  try {
    const [cssResponse, fontResponse] = await Promise.all([
      fetch(`${fontBase}style.css`),
      fetch(`${fontBase}fonts/feather2.ttf`)
    ]);
    const cssText = await cssResponse.text();
    const fontBuffer = await fontResponse.arrayBuffer();
    const dataUri = `data:font/truetype;base64,${bufferToBase64(fontBuffer)}`;
    captureFontCss = cssText.replace(
      /url\(['"]?fonts\/feather2\.ttf['"]?\)/,
      `url(${dataUri})`
    );
  } catch (_error) {
    captureFontCss = '';
  }

  return captureFontCss;
}

/**
 * Sets the canvas up to convert it into an image.
 *
 * @returns A function that undoes all of it.
 */
async function prepareCaptureUi(
  widget: CustomDiagramWidget,
  domCanvas: JQuery<HTMLCanvasElement>
): Promise<() => void> {
  widget.getFlowModel().clearSelection();
  widget.clearLinkTerminals();

  const visibleWrappers = $('.link_wrapper:visible');
  visibleWrappers.hide();
  domCanvas.addClass('capture-export');

  let fontStyleTag: HTMLStyleElement | null = null;
  const fontCss = await getCaptureFontCss();
  if (fontCss) {
    fontStyleTag = document.createElement('style');
    fontStyleTag.dataset.captureFonts = 'true';
    fontStyleTag.textContent = fontCss;
    domCanvas[0].appendChild(fontStyleTag);
  }

  return () => {
    domCanvas.removeClass('capture-export');
    fontStyleTag?.remove();
    visibleWrappers.show();
  };
}

/** Computes the offset needed to shift the active flow's nodes into positive coordinate space with margin. */
function getFlowOffset(widget: CustomDiagramWidget): Point {
  const { version, versions } = widget.getDataModel();
  const flow = versions[version].flow;

  if (!flow?.nodes?.length) {
    throw new Error('No nodes in the active flow.');
  }

  const xs = flow.nodes.map(({ x }) => x);
  const ys = flow.nodes.map(({ y }) => y);
  const minX = Math.min(...xs, 0);
  const minY = Math.min(...ys, 0);
  const shift = (minX < 0 || minY < 0) ? edgeMargin : 0;

  return {
    x: flowMargin - minX + shift,
    y: flowMargin - minY + shift
  };
}

/** Measures the bounding box (relative to the canvas) of all rendered flow elements, excluding link terminals and points; throws if nothing is rendered. */
function measureBounds(canvasEl: HTMLCanvasElement): {
  height: number;
  left: number;
  top: number;
  width: number;
} {
  const canvasRect = canvasEl.getBoundingClientRect();
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;

  canvasEl.querySelectorAll(boundsSelector).forEach((element) => {
    if (element.closest('.link-terminal-view') || element.classList.contains('point')) {
      return;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 && rect.height <= 0) {
      return;
    }

    left = Math.min(left, rect.left - canvasRect.left);
    top = Math.min(top, rect.top - canvasRect.top);
    right = Math.max(right, rect.right - canvasRect.left);
    bottom = Math.max(bottom, rect.bottom - canvasRect.top);
  });

  if (!Number.isFinite(left)) {
    throw new Error('No rendered nodes or links on the canvas.');
  }

  return {
    height: bottom - top,
    left,
    top,
    width: right - left
  };
}

/** Applies the diagram model's current offset/zoom to the canvas background position and link-terminal transform, then forces a re-render. */
function syncTransform(
  widget: CustomDiagramWidget,
  domCanvas: JQuery<HTMLCanvasElement>
): void {
  const diagramModel = widget.getFlowModel();
  const offsetX = diagramModel.getOffsetX();
  const offsetY = diagramModel.getOffsetY();
  const zoomFactor = diagramModel.getZoomLevel() / 100;

  domCanvas.css({
    'background-position-x': `${offsetX}px`,
    'background-position-y': `${offsetY}px`
  });
  domCanvas.children('.link-terminal-view').css(
    'transform',
    `translate(${offsetX}px,${offsetY}px) scale(${zoomFactor})`
  );
  widget.forceUpdate();
}

/** Shifts the diagram's offset so the measured bounds line up with the capture margin at the canvas origin. */
function alignOffset(
  widget: CustomDiagramWidget,
  { left, top }: { left: number; top: number }
): void {
  const diagramModel = widget.getFlowModel();

  diagramModel.setOffset(
    diagramModel.getOffsetX() + (captureMargin - left),
    diagramModel.getOffsetY() + (captureMargin - top)
  );
}

/** Snapshots the canvas's size, scroll position, and diagram offset/zoom, returning a function that restores all of it and resyncs the transform/canvas center. */
function saveCanvasState(
  widget: CustomDiagramWidget,
  domCanvas: JQuery<HTMLCanvasElement>
): () => void {
  const canvasEl = domCanvas[0];
  const diagramModel = widget.getFlowModel();
  const saved = {
    height: canvasEl.style.height,
    offsetX: diagramModel.getOffsetX(),
    offsetY: diagramModel.getOffsetY(),
    overflow: domCanvas.css('overflow'),
    scrollLeft: canvasEl.scrollLeft,
    scrollTop: canvasEl.scrollTop,
    width: canvasEl.style.width,
    zoom: diagramModel.getZoomLevel()
  };

  return () => {
    diagramModel.setZoomLevel(saved.zoom);
    diagramModel.setOffset(saved.offsetX, saved.offsetY);
    canvasEl.scrollTop = saved.scrollTop;
    canvasEl.scrollLeft = saved.scrollLeft;
    domCanvas.css({
      height: saved.height || '100vh',
      overflow: saved.overflow || '',
      width: saved.width || 'auto'
    });
    syncTransform(widget, domCanvas);
    window.apm.getCurrentDiagramWidget().setCanvasCenter();
  };
}

/** Reads the canvas's computed background color, returning `null` if it's unset or transparent. */
function readBackground(canvasEl: HTMLCanvasElement): string | null {
  const color = getComputedStyle(canvasEl).backgroundColor;
  return (!color || color === transparent) ? null : color;
}

/**
 * Capture the active flow canvas and download as PNG.
 * @param filename - Base name for the downloaded file
 */
export async function captureCanvasImage(
  widget: CustomDiagramWidget,
  filename: string
): Promise<void> {
  Utility.openApplicationOverlay('Generating image...');
  const domCanvas = widget.getCanvas();
  const canvasEl = domCanvas[0];
  const restoreCanvas = saveCanvasState(widget, domCanvas);
  let restoreUi: (() => void) | null = null;

  try {
    restoreUi = await prepareCaptureUi(widget, domCanvas);

    if (document.fonts?.ready) {
      await document.fonts.ready.catch(() => {});
    }

    widget.zoomDiagram({}, 1, true, getFlowOffset(widget));
    widget.handleCustomEvent({ target: 'request-refresh' });
    await waitFrames(4);

    const bounds = measureBounds(canvasEl);
    alignOffset(widget, bounds);
    syncTransform(widget, domCanvas);
    widget.handleCustomEvent({ target: 'request-refresh' });
    await waitFrames();

    domCanvas.css({
      height: `${Math.ceil(bounds.height + (captureMargin * 2))}px`,
      overflow: 'visible',
      width: `${Math.ceil(bounds.width + (captureMargin * 2))}px`
    });
    canvasEl.scrollTop = 0;
    canvasEl.scrollLeft = 0;
    await waitFrames();

    const background = readBackground(canvasEl);
    const restoreHost = mountCaptureHost(canvasEl);
    await waitFrames();

    let shot: HTMLCanvasElement | null = null;
    try {
      shot = await rasterizeCanvas(canvasEl, background);
    } finally {
      restoreHost();
    }

    FileSaver.saveAs(
      await canvasToBlob(trimCanvas(shot, background)),
      `${filename}_image.png`
    );
  } catch (error) {
    console.error('something went wrong downloading the image!', error);
  } finally {
    restoreUi?.();
    restoreCanvas();
    window.apm.refreshApplicationTree();
    widget.handleCustomEvent({ target: 'request-update-member' });
    window.dispatchEvent(new Event('resize'));
    widget.handleCustomEvent({ target: 'request-refresh' });
    await waitFrames();
    Utility.closeApplicationOverlay();
  }
}
